import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Video, User, Building2, Briefcase, Loader2, 
  Check, AlertCircle, Play, Square, RotateCcw, Upload
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TestimonialRecord() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // Request data
  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Form data
  const [recorderName, setRecorderName] = useState('');
  const [recorderCompany, setRecorderCompany] = useState('');
  const [recorderDesignation, setRecorderDesignation] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  
  // Recording state
  const [step, setStep] = useState('details'); // details, recording, preview, uploading, success
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [timer, setTimer] = useState(45);
  const [uploading, setUploading] = useState(false);
  
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  // Fetch testimonial request details
  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/testimonial/${token}`);
        setRequestData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'This testimonial link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchRequest();
  }, [token]);

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setError('Unable to access camera. Please ensure you have granted camera and microphone permissions.');
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  // Start recording
  const startRecording = () => {
    if (!streamRef.current) return;
    
    chunksRef.current = [];
    const options = { mimeType: 'video/webm;codecs=vp9,opus' };
    
    try {
      mediaRecorderRef.current = new MediaRecorder(streamRef.current, options);
    } catch (e) {
      mediaRecorderRef.current = new MediaRecorder(streamRef.current);
    }
    
    mediaRecorderRef.current.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };
    
    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
      setStep('preview');
    };
    
    mediaRecorderRef.current.start(1000);
    setIsRecording(true);
    setTimer(45);
    
    // Timer countdown
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          stopRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Stop recording
  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    stopCamera();
  };

  // Retake
  const handleRetake = async () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setStep('recording');
    setTimer(45);
    await startCamera();
  };

  // Submit
  const handleSubmit = async () => {
    if (!recordedBlob || !consentGiven) return;
    
    setUploading(true);
    setStep('uploading');
    
    const formData = new FormData();
    formData.append('video', recordedBlob, 'testimonial.webm');
    formData.append('recorder_name', recorderName);
    formData.append('recorder_company', recorderCompany);
    formData.append('recorder_designation', recorderDesignation);
    formData.append('consent', consentGiven ? 'true' : '');
    
    try {
      await axios.post(`${API_URL}/api/testimonial/${token}/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStep('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload testimonial. Please try again.');
      setStep('preview');
    } finally {
      setUploading(false);
    }
  };

  // Proceed to recording
  const proceedToRecording = async () => {
    if (!recorderName.trim() || !recorderCompany.trim() || !recorderDesignation.trim()) {
      setError('Please fill in all fields');
      return;
    }
    setError('');
    setStep('recording');
    await startCamera();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      if (timerRef.current) clearInterval(timerRef.current);
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
  }, [recordedUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C41E3A]" />
      </div>
    );
  }

  if (error && !requestData) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Link Invalid</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 py-4">
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-center gap-3">
          <img 
            src="https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png" 
            alt="BrandStage" 
            className="h-8"
          />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Profile Info */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2">
            Record a Testimonial for
          </h1>
          <p className="text-3xl font-bold text-[#C41E3A]">
            {requestData?.profile_name}
          </p>
          {requestData?.message && (
            <div className="mt-4 bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 text-zinc-400 italic">
              &quot;{requestData.message}&quot;
            </div>
          )}
        </div>

        {error && step !== 'success' && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Step: Details */}
        {step === 'details' && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-6">Your Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  Your Name *
                </label>
                <input
                  type="text"
                  value={recorderName}
                  onChange={(e) => setRecorderName(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  placeholder="e.g., John Smith"
                  data-testid="recorder-name"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">
                  <Briefcase className="w-4 h-4 inline mr-2" />
                  Your Designation *
                </label>
                <input
                  type="text"
                  value={recorderDesignation}
                  onChange={(e) => setRecorderDesignation(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  placeholder="e.g., CEO, Marketing Director"
                  data-testid="recorder-designation"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">
                  <Building2 className="w-4 h-4 inline mr-2" />
                  Your Company *
                </label>
                <input
                  type="text"
                  value={recorderCompany}
                  onChange={(e) => setRecorderCompany(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  placeholder="e.g., Acme Corporation"
                  data-testid="recorder-company"
                />
              </div>
            </div>
            
            <button
              onClick={proceedToRecording}
              className="w-full mt-6 bg-[#C41E3A] hover:bg-[#A01830] text-white py-4 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-2"
              data-testid="proceed-to-record-btn"
            >
              <Video className="w-5 h-5" />
              Continue to Recording
            </button>
            
            <p className="text-center text-zinc-500 text-sm mt-4">
              You&apos;ll have 45 seconds to share your testimonial
            </p>
          </div>
        )}

        {/* Step: Recording */}
        {step === 'recording' && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden mb-4">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover"
              />
              
              {/* Timer overlay */}
              <div className={`absolute top-4 right-4 px-4 py-2 rounded-lg font-mono text-xl font-bold ${
                timer <= 5 ? 'bg-red-500 animate-pulse' : 'bg-black/70'
              }`}>
                0:{timer.toString().padStart(2, '0')}
              </div>
              
              {/* Recording indicator */}
              {isRecording && (
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500 px-3 py-1 rounded-lg">
                  <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                  REC
                </div>
              )}
            </div>
            
            <div className="text-center">
              {!isRecording ? (
                <button
                  onClick={startRecording}
                  className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-8 py-4 rounded-full font-bold text-lg transition-colors flex items-center gap-2 mx-auto"
                  data-testid="start-recording-btn"
                >
                  <Play className="w-6 h-6" />
                  Start Recording
                </button>
              ) : (
                <button
                  onClick={stopRecording}
                  className="bg-zinc-700 hover:bg-zinc-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-colors flex items-center gap-2 mx-auto"
                  data-testid="stop-recording-btn"
                >
                  <Square className="w-6 h-6" />
                  Stop Recording
                </button>
              )}
              
              <p className="text-zinc-400 mt-4">
                {!isRecording 
                  ? "Click to start. Share your experience and what you value about working with them."
                  : "Recording... Click stop when you're done, or it will auto-stop at 45 seconds."
                }
              </p>
            </div>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-4">Preview Your Testimonial</h2>
            
            <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
              <video
                ref={previewRef}
                src={recordedUrl}
                controls
                className="w-full h-full"
              />
            </div>
            
            <label className="flex items-start gap-3 mb-4 cursor-pointer bg-zinc-800/50 border border-zinc-700 rounded-lg p-4" data-testid="testimonial-consent">
              <input type="checkbox" checked={consentGiven} onChange={(e) => setConsentGiven(e.target.checked)} className="mt-1 w-5 h-5 shrink-0 accent-[#C41E3A]" data-testid="testimonial-consent-checkbox" />
              <span className="text-sm text-zinc-300">
                I consent to this video, my name, company and designation being recorded, stored and published on the requesting company's BrandStage profile and shared by them. I confirm the views are my own. I can ask for it to be removed anytime by writing to brandstagevideo@gmail.com. See the <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#C41E3A] underline">Privacy Policy</a> (DPDP Act 2023 / GDPR).
              </span>
            </label>
            
            <div className="flex gap-4">
              <button
                onClick={handleRetake}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-2"
                data-testid="retake-btn"
              >
                <RotateCcw className="w-5 h-5" />
                Retake
              </button>
              <button
                onClick={handleSubmit}
                disabled={!consentGiven}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 disabled:cursor-not-allowed text-white py-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-2"
                data-testid="submit-testimonial-btn"
              >
                <Upload className="w-5 h-5" />
                Submit Testimonial
              </button>
            </div>
          </div>
        )}

        {/* Step: Uploading */}
        {step === 'uploading' && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-[#C41E3A] mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Uploading Your Testimonial</h2>
            <p className="text-zinc-400">Please wait while we process your video...</p>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
            <p className="text-zinc-400 mb-6">
              Your testimonial has been submitted successfully. It will be reviewed and may appear on {requestData?.profile_name}&apos;s profile.
            </p>
            <p className="text-zinc-500 text-sm">
              You can close this window now.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
